import { TableView } from '@tiptap/extension-table';
import { measureColumnWidths } from '@/components/content/tiptap/measureColumnWidths';
import { applyColumnPercentages } from '@/lib/applyColumnPercentages';
import { resolveColumnWidthsForTable } from '@/lib/tableUtils';

// A table node view that never lets a table grow wider than its container.

// Rendered width, or 0 before layout. Guards the method's existence - a detached table would throw.
const renderedWidth = (table) =>
  typeof table.getBoundingClientRect === 'function' ? table.getBoundingClientRect().width : 0;

const hasLayout = (table) => renderedWidth(table) > 0;

/**
 * Column widths from the node's `colwidth` attributes, from the row with the most recorded widths.
 * `null` only when there is nothing to read.
 *
 * Rejecting rows that contained an unsized column made `constrainToContainer` give up entirely, so such
 * a table stayed at raw pixel widths until a click triggered an update.
 */
const columnWidthsFromNode = (node) => {
  if (!node || typeof node.childCount !== 'number' || typeof node.child !== 'function') {
    return null;
  }

  let best = null;
  let bestColumnCount = 0;
  let bestRecordedCount = -1;

  for (let rowIndex = 0; rowIndex < node.childCount; rowIndex += 1) {
    const row = node.child(rowIndex);
    const cellCount = row && typeof row.childCount === 'number' ? row.childCount : 0;
    const widths = [];

    for (let cellIndex = 0; cellIndex < cellCount; cellIndex += 1) {
      const { colspan, colwidth } = row.child(cellIndex).attrs || {};
      const span = Math.max(colspan || 1, 1);

      for (let offset = 0; offset < span; offset += 1) {
        // Zero for an unsized column; raised later.
        widths.push((colwidth && colwidth[offset]) || 0);
      }
    }

    const recordedCount = widths.filter((width) => width > 0).length;

    // Most recorded widths wins, then the wider row - unsized cells must not displace the author's.
    if (
      recordedCount > bestRecordedCount ||
      (recordedCount === bestRecordedCount && widths.length > bestColumnCount)
    ) {
      bestColumnCount = widths.length;
      bestRecordedCount = recordedCount;
      best = widths;
    }
  }

  return best;
};

/**
 * Stored widths first, measurement only as a fallback.
 *
 * The table is measured only where the answer depends on it. Measuring up front reads layout while
 * the colgroup still holds the pixel widths `updateColumns` just wrote, which commits a transient
 * layout as wide as the sum of those widths. In the printable preview that is wider than the page,
 * so the columns wrap less, the document is shorter, and the browser clamps the scroll position to
 * it - a hover then leaves the page scrolled up, and repeated hovers walk it to the top of the table.
 * The percentages written straight afterwards restore the height but not the lost scroll offset.
 */
const resolveColumnWidths = (table, columnCount, node) => {
  const stored = columnWidthsFromNode(node);

  // The table's own width anchors the minimum, as `data-wrapperwidth` does on the render path.
  if (stored && stored.length > 0) {
    if (stored.every((width) => width > 0)) {
      return stored;
    }

    return resolveColumnWidthsForTable(stored, renderedWidth(table));
  }

  if (hasLayout(table)) {
    const measured = measureColumnWidths(table);

    if (measured.length === columnCount) {
      return resolveColumnWidthsForTable(measured, renderedWidth(table));
    }
  }

  return null;
};

// Keep the table inside its container, in every state the resizer can leave it in.
const constrainToContainer = (table, colgroup, node) => {
  if (!table || !colgroup || colgroup.children.length === 0) {
    return;
  }

  table.style.width = '100%';
  table.style.minWidth = '';

  const widths = resolveColumnWidths(table, colgroup.children.length, node);

  if (!widths) {
    return;
  }

  // A column with no `col` gets whatever fixed layout leaves over - nothing, once the sized columns
  // have taken their share. Give every real column an element to carry its width.
  while (colgroup.children.length < widths.length) {
    colgroup.appendChild(colgroup.ownerDocument.createElement('col'));
  }

  applyColumnPercentages(colgroup, widths);
};

export class BoundedTableView extends TableView {
  constructor(node, cellMinWidth) {
    super(node, cellMinWidth);
    constrainToContainer(this.table, this.colgroup, node);
    this.scheduleLayoutPass();
  }

  // Re-run containment once the browser has laid the table out.
  scheduleLayoutPass() {
    if (typeof requestAnimationFrame !== 'function') {
      return;
    }

    this.layoutPass = requestAnimationFrame(() => {
      this.layoutPass = null;
      constrainToContainer(this.table, this.colgroup, this.node);
    });
  }

  update(node) {
    const updated = super.update(node);

    if (updated) {
      constrainToContainer(this.table, this.colgroup, node);
    }

    return updated;
  }

  destroy() {
    if (this.layoutPass && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.layoutPass);
      this.layoutPass = null;
    }
  }
}

export default BoundedTableView;
